import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from 'sonner';
import { Modal } from "../components/Modal";
import { CircleCheck, CircleX } from "lucide-react";
import { useAuth } from "../contexts/useAuth";
import { useTranslation } from "../contexts/TranslationContext";

function Admin() {
    const [usuarios, setUsuarios] = useState([]);
    const navigate = useNavigate();
    const { token, logout } = useAuth();
    const { t } = useTranslation();
    const hasFetchedData = useRef(false);

    const [modal, setModal] = useState({ open: false, message: "", variant: "default", onConfirm: null });

    function openModal(message, variant, onConfirm) {
        setModal({ open: true, message, variant, onConfirm });
    }

    function closeModal() {
        setModal({ open: false, message: "", variant: "default", onConfirm: null });
    }

    async function handleLiberar(email) {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/liberar`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ email }),
            });

            if (response.ok) {
                toast.success(t('admin.userReleased'));
                setUsuarios(usuarios.map((u) =>
                    u.email === email ? { ...u, isPaid: true } : u
                ));
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || t('admin.releaseError'));
            }
        } catch (error) {
            console.error("Erro ao liberar usuário:", error);
            toast.error(t('admin.internalError'));
        }
    }

    function handleDesativar(email) {
        openModal(`${t('modals.deactivateUser')} ${email}?`, "default", async () => {
            closeModal();
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/desativar`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ email }),
                });

                if (response.ok) {
                    toast.success(t('admin.userDeactivated'));
                    setUsuarios(usuarios.map((u) =>
                        u.email === email ? { ...u, isPaid: false } : u
                    ));
                } else {
                    const errorData = await response.json();
                    toast.error(errorData.error || t('admin.deactivateError'));
                }
            } catch (error) {
                console.error("Erro ao desativar usuário:", error);
                toast.error(t('admin.internalError'));
            }
        });
    }

    function handleExcluir(user) {
        openModal(`${t('modals.deleteUserMessage')} ${user.email}? ${t('modals.deleteUserWarning')}`, "danger", async () => {
            closeModal();
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/users/${user._id}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    toast.success(t('admin.userDeleted'));
                    setUsuarios(usuarios.filter((u) => u._id !== user._id));
                } else {
                    const errorData = await response.json();
                    toast.error(errorData.error || t('admin.deleteError'));
                }
            } catch (error) {
                console.error("Erro ao excluir usuário:", error);
                toast.error(t('admin.internalError'));
            }
        });
    }

    useEffect(() => {
        if (hasFetchedData.current) return;

        async function fetchUsuarios() {
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/users`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.status === 401 || response.status === 403) {
                    toast.error(t('admin.accessDenied'));
                    logout();
                    navigate("/login");
                    return;
                }

                const data = await response.json();
                setUsuarios(data);
                hasFetchedData.current = true;
            } catch (error) {
                console.error("Erro ao buscar usuários:", error);
                toast.error(t('admin.loadError'));
            }
        }

        fetchUsuarios();
    }, [token, navigate]);

    return (
        <div className="flex-1 bg-[#0a0a0a] text-white px-6 pt-12 pb-6">
            <div className="max-w-5xl mx-auto">
                <h1 className="text-2xl font-bold text-white mb-6">
                    {t('admin.title')}
                </h1>

                <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="min-w-full table-auto">
                    <thead className="bg-white/5">
                        <tr>
                            <th className="text-left py-3 px-4 text-sm sm:text-base text-gray-300 font-medium">{t('admin.email')}</th>
                            <th className="text-center py-3 px-4 text-sm sm:text-base text-gray-300 font-medium">{t('admin.credits')}</th>
                            <th className="text-center py-3 px-4 text-sm sm:text-base text-gray-300 font-medium">{t('admin.paid')}</th>
                            <th className="text-center py-3 px-4 text-sm sm:text-base text-gray-300 font-medium">{t('admin.isAdmin')}</th>
                            <th className="text-center py-3 px-4 text-sm sm:text-base text-gray-300 font-medium">{t('admin.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usuarios.map((user, index) => (
                            <tr
                                key={user._id || index}
                                className={`border-t border-white/5 ${index % 2 === 0 ? "bg-[#111]" : "bg-[#0a0a0a]"}`}
                            >
                                <td className="py-2 px-4 text-sm sm:text-base text-gray-200">{user.email}</td>
                                <td className="py-2 px-4 text-center text-sm sm:text-base text-gray-300">{user.credits ?? 0}</td>
                                <td className="py-2 px-4 text-center text-sm sm:text-base">
                                    {user.isPaid ? <CircleCheck className="inline text-green-500" size={20} /> : <CircleX className="inline text-red-500" size={20} />}
                                </td>
                                <td className="py-2 px-4 text-center text-sm sm:text-base">
                                    {user.isAdmin ? <CircleCheck className="inline text-green-500" size={20} /> : <CircleX className="inline text-red-500" size={20} />}
                                </td>
                                <td className="py-2 px-4 text-center">
                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                        {!user.isPaid ? (
                                            <button
                                                onClick={() => handleLiberar(user.email)}
                                                className="bg-green-500 hover:bg-green-600 text-white font-semibold px-3 py-1 rounded transition text-sm"
                                            >
                                                {t('admin.release')}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleDesativar(user.email)}
                                                className="bg-brand-500 hover:bg-brand-600 text-black font-semibold px-3 py-1 rounded transition text-sm"
                                            >
                                                {t('admin.deactivate')}
                                            </button>
                                        )}
                                        {!user.isAdmin && (
                                            <button
                                                onClick={() => handleExcluir(user)}
                                                className="bg-red-500 hover:bg-red-600 text-white font-semibold px-3 py-1 rounded transition text-sm"
                                            >
                                                {t('admin.deleteUser')}
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={modal.open}
                onClose={closeModal}
                onConfirm={modal.onConfirm}
                message={modal.message}
                confirmText={t('modals.confirm')}
                cancelText={t('modals.cancel')}
                variant={modal.variant}
            />
            </div>
        </div>
    );
}

export default Admin;
